import React, { useState } from 'react';
import { Popover } from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Liste déroulante à choix multiples, avec recherche.
 * @param {string} label - Nom de l'axe
 * @param {Array<string>} options - Valeurs proposées
 * @param {Array<string>} value - Valeurs cochées (vide = pas de filtre)
 * @param {Function} onChange - Reçoit la nouvelle liste
 * @param {Function} [format] - Libellé affiché pour une valeur
 */
const MultiSelect = ({ label, options, value, onChange, format = (v) => v }) => {
  const [search, setSearch] = useState('');
  const shown = options.filter((o) => format(o).toLowerCase().includes(search.toLowerCase()));
  const toggle = (option) =>
    onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option]);
  const active = value.length > 0;

  return (
    <Popover className="relative">
      <Popover.Button
        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
          active
            ? 'border-primary-600 bg-primary-50 text-primary-800 font-medium'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        {label}
        {active && (
          <span className="rounded-full bg-primary-600 px-1.5 text-xs font-semibold text-white">{value.length}</span>
        )}
        <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
      </Popover.Button>

      <Popover.Panel className="absolute z-30 mt-1 w-72 rounded-md border border-gray-200 bg-white shadow-lg">
        <div className="border-b border-gray-100 p-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Rechercher (${options.length})`}
            aria-label={`Rechercher dans ${label}`}
            className="block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
        <ul className="max-h-64 overflow-y-auto py-1">
          {shown.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">Aucune valeur</li>}
          {shown.map((option) => (
            <li key={option}>
              <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={value.includes(option)}
                  onChange={() => toggle(option)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="truncate" title={format(option)}>{format(option)}</span>
              </label>
            </li>
          ))}
        </ul>
        {active && (
          <div className="border-t border-gray-100 p-2">
            <button
              type="button"
              onClick={() => onChange([])}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
            >
              <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Effacer ce filtre
            </button>
          </div>
        )}
      </Popover.Panel>
    </Popover>
  );
};

export default MultiSelect;
